/**
 * TennisCourtSvg — Full-court visualization with opponent-side targeting.
 *
 * Tennis is a game of hitting INTO the opponent's court.
 * Top half = opponent's court (target zones highlighted).
 * Bottom half = your side (player position).
 *
 * Real proportions: full court 23.77m × 8.23m (singles) / 10.97m (doubles).
 * viewBox 500 × 700: court area 70..430 × 30..670.
 */

import styles from "./SportSvg.module.css";

/*
 * Coordinate system (viewBox 500 × 700):
 * - Doubles sidelines: x=50..450
 * - Singles sidelines: x=120..380
 * - Net at y=350 (center)
 * - Opponent's baseline at y=30
 * - Opponent's service line at y=200
 * - Your service line at y=500
 * - Your baseline at y=670
 * - Center service line: x=250
 */

/* ── Opponent's service box zones (where YOUR serves land) ── */
const SVC_ZONES = [
  /* Deuce side: x=120..250 */
  {
    id: "d-t",
    label: "T",
    x: 120,
    y: 200,
    w: 44,
    h: 148,
    pct: 42,
    color: "#34c759",
    desc: "Deuce T",
  },
  {
    id: "d-body",
    label: "Body",
    x: 164,
    y: 200,
    w: 42,
    h: 148,
    pct: 28,
    color: "#ffd740",
    desc: "Deuce Body",
  },
  {
    id: "d-wide",
    label: "Wide",
    x: 206,
    y: 200,
    w: 44,
    h: 148,
    pct: 30,
    color: "#66bb6a",
    desc: "Deuce Wide",
  },
  /* Ad side: x=250..380 */
  {
    id: "a-wide",
    label: "Wide",
    x: 250,
    y: 200,
    w: 44,
    h: 148,
    pct: 34,
    color: "#66bb6a",
    desc: "Ad Wide",
  },
  {
    id: "a-body",
    label: "Body",
    x: 294,
    y: 200,
    w: 42,
    h: 148,
    pct: 26,
    color: "#ffd740",
    desc: "Ad Body",
  },
  {
    id: "a-t",
    label: "T",
    x: 336,
    y: 200,
    w: 44,
    h: 148,
    pct: 40,
    color: "#34c759",
    desc: "Ad T",
  },
];

/* ── Opponent's backcourt zones (where YOUR rally shots land) ── */
const RALLY_ZONES = [
  {
    id: "deep-deuce",
    label: "Deep",
    x: 120,
    y: 30,
    w: 130,
    h: 85,
    pct: 45,
    color: "#ffcc00",
  },
  {
    id: "deep-ad",
    label: "Deep",
    x: 250,
    y: 30,
    w: 130,
    h: 85,
    pct: 51,
    color: "#ffd740",
  },
  {
    id: "mid-deuce",
    label: "Mid",
    x: 120,
    y: 115,
    w: 130,
    h: 85,
    pct: 38,
    color: "#ff9500",
  },
  {
    id: "mid-ad",
    label: "Mid",
    x: 250,
    y: 115,
    w: 130,
    h: 85,
    pct: 42,
    color: "#ff9500",
  },
];

export function TennisCourtSvg() {
  return (
    <svg
      viewBox="0 0 500 720"
      className={styles.svg}
      role="img"
      aria-label="Tennis full-court analytics — opponent-side targeting"
    >
      <title>
        Tennis full court — serve target zones and rally shot placement on opponent's side
      </title>
      <defs>
        <pattern id="tc-net" x="0" y="0" width="8" height="6" patternUnits="userSpaceOnUse">
          <line x1="0" y1="0" x2="8" y2="6" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
          <line x1="8" y1="0" x2="0" y2="6" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
        </pattern>
        <linearGradient id="tc-opp" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(0,100,60,0.08)" />
          <stop offset="100%" stopColor="rgba(0,100,60,0.03)" />
        </linearGradient>
        <linearGradient id="tc-own" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(0,100,60,0.03)" />
          <stop offset="100%" stopColor="rgba(0,100,60,0.06)" />
        </linearGradient>
      </defs>

      {/* ══════ Court surfaces ══════ */}
      {/* Opponent's half (highlighted) */}
      <rect x="50" y="20" width="400" height="330" rx="2" fill="url(#tc-opp)" />
      {/* Your half (subdued) */}
      <rect x="50" y="350" width="400" height="330" rx="2" fill="url(#tc-own)" />

      {/* ── Doubles sidelines (full length) ── */}
      <line x1="50" y1="20" x2="50" y2="680" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      <line x1="450" y1="20" x2="450" y2="680" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />

      {/* ── Singles sidelines ── */}
      <line x1="120" y1="20" x2="120" y2="680" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
      <line x1="380" y1="20" x2="380" y2="680" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />

      {/* ══════ Opponent's court ══════ */}
      {/* Opponent baseline */}
      <line x1="120" y1="30" x2="380" y2="30" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
      <line x1="250" y1="20" x2="250" y2="30" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />

      {/* Opponent service line */}
      <line x1="120" y1="200" x2="380" y2="200" stroke="rgba(255,255,255,0.4)" strokeWidth="2" />

      {/* Opponent center service line */}
      <line x1="250" y1="200" x2="250" y2="350" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />

      {/* ══════ Net ══════ */}
      <rect x="40" y="344" width="420" height="12" fill="url(#tc-net)" />
      <line x1="40" y1="350" x2="460" y2="350" stroke="rgba(255,255,255,0.7)" strokeWidth="3" />
      <rect x="36" y="342" width="8" height="16" rx="2" fill="rgba(255,255,255,0.45)" />
      <rect x="456" y="342" width="8" height="16" rx="2" fill="rgba(255,255,255,0.45)" />
      <text x="472" y="354" className={styles.zoneLabel} fill="rgba(255,255,255,0.5)">
        NET
      </text>

      {/* ══════ Your court ══════ */}
      {/* Your service line */}
      <line x1="120" y1="500" x2="380" y2="500" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />

      {/* Your center service line */}
      <line x1="250" y1="350" x2="250" y2="500" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />

      {/* Your baseline */}
      <line x1="120" y1="670" x2="380" y2="670" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
      <line x1="250" y1="670" x2="250" y2="680" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />

      {/* ══════ Rally zones (opponent's backcourt) ══════ */}
      {RALLY_ZONES.map((z) => (
        <g key={z.id}>
          <rect
            x={z.x + 1}
            y={z.y + 1}
            width={z.w - 2}
            height={z.h - 2}
            rx="2"
            fill={`${z.color}0A`}
            stroke={`${z.color}20`}
            strokeWidth="1"
          />
          <text
            x={z.x + z.w / 2}
            y={z.y + z.h / 2 - 6}
            textAnchor="middle"
            className={styles.zoneLabel}
            fill="rgba(255,255,255,0.3)"
          >
            {z.label}
          </text>
          <text
            x={z.x + z.w / 2}
            y={z.y + z.h / 2 + 10}
            textAnchor="middle"
            className={styles.zonePct}
            fill={z.color}
          >
            {z.pct}%
          </text>
        </g>
      ))}

      {/* ══════ Service box zones (T/Body/Wide) ══════ */}
      {SVC_ZONES.map((z) => (
        <g key={z.id}>
          <rect
            x={z.x + 1}
            y={z.y + 1}
            width={z.w - 2}
            height={z.h - 2}
            rx="2"
            fill={`${z.color}0E`}
            stroke={`${z.color}28`}
            strokeWidth="1"
          />
          <text
            x={z.x + z.w / 2}
            y={z.y + z.h / 2 - 8}
            textAnchor="middle"
            className={styles.zoneLabel}
            fill="rgba(255,255,255,0.3)"
          >
            {z.label}
          </text>
          <text
            x={z.x + z.w / 2}
            y={z.y + z.h / 2 + 10}
            textAnchor="middle"
            className={styles.zonePct}
            fill={z.color}
          >
            {z.pct}%
          </text>
        </g>
      ))}

      {/* ── Side labels on opponent's court ── */}
      <text
        x="185"
        y="365"
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.2)"
      >
        Deuce
      </text>
      <text
        x="315"
        y="365"
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.2)"
      >
        Ad
      </text>

      {/* ── T-zone marker ── */}
      <circle
        cx="250"
        cy="350"
        r="5"
        fill="rgba(255,255,255,0.1)"
        stroke="rgba(255,255,255,0.2)"
        strokeWidth="1"
      />

      {/* ══════ Shot landing indicators (on opponent's court) ══════ */}
      {/* Service aces/wins */}
      <circle cx="148" cy="260" r="5" fill="#34c759" opacity="0.9" />
      <circle cx="355" cy="240" r="5" fill="#34c759" opacity="0.9" />
      <circle cx="275" cy="300" r="5" fill="#66bb6a" opacity="0.85" />
      <circle cx="220" cy="320" r="5" fill="#ffd740" opacity="0.85" />
      {/* Rally winners */}
      <circle cx="165" cy="55" r="5" fill="#ffcc00" opacity="0.85" />
      <circle cx="310" cy="70" r="5" fill="#ffcc00" opacity="0.85" />
      <circle cx="350" cy="140" r="5" fill="#ff9500" opacity="0.85" />
      <circle cx="140" cy="150" r="5" fill="#ff9500" opacity="0.85" />
      {/* Faults/out (outside lines) */}
      <circle
        cx="108"
        cy="250"
        r="5"
        fill="none"
        stroke="#ff3b30"
        strokeWidth="1.5"
        opacity="0.8"
      />
      <circle
        cx="392"
        cy="310"
        r="5"
        fill="none"
        stroke="#ff3b30"
        strokeWidth="1.5"
        opacity="0.8"
      />

      {/* ══════ Player position (your baseline) ══════ */}
      <g>
        <circle
          cx="250"
          cy="650"
          r="14"
          fill="rgba(33,150,243,0.12)"
          stroke="#2196f3"
          strokeWidth="2"
        />
        <circle cx="250" cy="643" r="5" fill="#2196f3" opacity="0.9" />
        <line
          x1="259"
          y1="648"
          x2="270"
          y2="638"
          stroke="#2196f3"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <ellipse
          cx="274"
          cy="634"
          rx="5"
          ry="7"
          fill="none"
          stroke="#2196f3"
          strokeWidth="1.5"
          transform="rotate(-20, 274, 634)"
        />
        <text x="250" y="678" textAnchor="middle" className={styles.zoneLabel} fill="#2196f3">
          YOU
        </text>
      </g>

      {/* ── Serve trajectories (from you → opponent's service boxes) ── */}
      <line
        x1="250"
        y1="640"
        x2="148"
        y2="260"
        stroke="rgba(33,150,243,0.25)"
        strokeWidth="1.5"
        strokeDasharray="6,4"
      />
      <polygon points="148,260 144,273 152,273" fill="rgba(33,150,243,0.4)" />

      <line
        x1="250"
        y1="640"
        x2="355"
        y2="240"
        stroke="rgba(33,150,243,0.15)"
        strokeWidth="1"
        strokeDasharray="4,4"
      />
      <polygon points="355,240 351,253 359,253" fill="rgba(33,150,243,0.25)" />

      {/* ── Rally trajectory (from you → opponent's deep court) ── */}
      <line
        x1="250"
        y1="640"
        x2="165"
        y2="55"
        stroke="rgba(255,204,0,0.15)"
        strokeWidth="1"
        strokeDasharray="4,4"
      />
      <polygon points="165,55 161,68 169,68" fill="rgba(255,204,0,0.25)" />

      {/* ── Doubles alley labels ── */}
      <text
        x="85"
        y="350"
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.15)"
        transform="rotate(-90, 85, 350)"
      >
        Doubles Alley
      </text>
      <text
        x="415"
        y="350"
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.15)"
        transform="rotate(90, 415, 350)"
      >
        Doubles Alley
      </text>

      {/* ── Court area labels ── */}
      <text
        x="250"
        y="15"
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.25)"
      >
        OPPONENT&apos;S COURT
      </text>
      <text
        x="250"
        y="698"
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(255,255,255,0.2)"
      >
        YOUR COURT
      </text>
    </svg>
  );
}
